import { Button, Input, Modal } from 'antd';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import s from './ReturnTaskModal.module.scss';

import type { FC } from 'react';

export interface IReturnTaskModalProps {
  open: boolean;
  isSubmitting: boolean;
  onSubmit: (returnNote: string) => void;
  onCancel: () => void;
}

const ReturnTaskModal: FC<IReturnTaskModalProps> = ({
  open,
  isSubmitting,
  onSubmit,
  onCancel,
}) => {
  const { t } = useTranslation();
  const [note, setNote] = useState('');
  const [touched, setTouched] = useState(false);

  const isBlank = note.trim() === '';
  const showError = touched && isBlank;

  const handleConfirm = useCallback(() => {
    setTouched(true);
    if (isBlank) return;
    onSubmit(note.trim());
  }, [isBlank, note, onSubmit]);

  const handleAfterClose = useCallback(() => {
    setNote('');
    setTouched(false);
  }, []);

  return (
    <Modal
      open={open}
      title={t('tasksDrawer.returnModal.title')}
      centered
      destroyOnClose
      maskClosable={false}
      zIndex={1100}
      onCancel={onCancel}
      afterClose={handleAfterClose}
      footer={null}
      data-cy="return-task-modal"
    >
      <div className={s['modal-body']}>
        <p className={s['modal-description']}>
          {t('tasksDrawer.returnModal.body')}
        </p>

        <div className={s['form-field']}>
          <label htmlFor="return-note-input" className={s['field-label']}>
            {t('tasksDrawer.returnModal.noteLabel')}{' '}
            <span className={s.required}>*</span>
          </label>
          <Input.TextArea
            id="return-note-input"
            value={note}
            onChange={(e) => {
              setNote(e.target.value);
              if (!touched) setTouched(true);
            }}
            autoSize={{ minRows: 3, maxRows: 6 }}
            placeholder={t('tasksDrawer.returnModal.notePlaceholder')}
            status={showError ? 'error' : undefined}
            data-cy="return-note-textarea"
          />
          {showError && (
            <p className={s['field-error']} data-cy="return-note-error">
              {t('tasksDrawer.returnModal.noteRequired')}
            </p>
          )}
        </div>
      </div>

      <div className={s['modal-footer']}>
        <Button onClick={onCancel} data-cy="return-modal-cancel-button">
          {t('tasksDrawer.returnModal.cancel')}
        </Button>
        <Button
          type="primary"
          onClick={handleConfirm}
          loading={isSubmitting}
          disabled={isSubmitting || isBlank}
          data-cy="return-modal-confirm-button"
        >
          {t('tasksDrawer.returnModal.confirm')}
        </Button>
      </div>
    </Modal>
  );
};

export default ReturnTaskModal;
